import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
} from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatDistanceToNow } from 'date-fns';
import AdaptiveImage from '../AdaptiveImage';
import ArticleResources from './ArticleResources';

const ARTICLE_TYPES = {
  article: { label: 'Article' },
  long_social_post: { label: 'Long Social Post' },
  short_social_post: { label: 'Short Social Post' },
  image_carousel: { label: 'Image Carousel' },
  short_video: { label: 'Short Video' },
  long_video: { label: 'Long Video' }
};

const ARTICLE_SUBTYPES = {
  // Article subtypes
  how_to: { label: 'How-To Guide' },
  list_article: { label: 'List Article' },
  review: { label: 'Review' },
  opinion: { label: 'Opinion' },
  news: { label: 'News' },
  case_study: { label: 'Case Study' },
  
  // Short social post subtypes
  instagram_post: { label: 'Instagram Post' },
  x_post: { label: 'X/Twitter Post' },
  facebook_post: { label: 'Facebook Post' },
  linkedin_short: { label: 'LinkedIn Short' },
  threads_post: { label: 'Threads Post' },
  
  // Long social post subtypes
  linkedin_article: { label: 'LinkedIn Article' },
  facebook_long: { label: 'Facebook Long Post' },
  medium_story: { label: 'Medium Story' },
  x_thread: { label: 'X/Twitter Thread' },
  
  // Image carousel subtypes
  instagram_carousel: { label: 'Instagram Carousel' },
  linkedin_carousel: { label: 'LinkedIn Carousel' },
  facebook_carousel: { label: 'Facebook Carousel' },
  
  // Short video subtypes
  tiktok: { label: 'TikTok' },
  instagram_reel: { label: 'Instagram Reel' },
  youtube_short: { label: 'YouTube Short' },
  
  // Long video subtypes
  youtube_video: { label: 'YouTube Video' },
  vimeo_video: { label: 'Vimeo Video' },
  course_video: { label: 'Course Video' }
};

const ARTICLE_STATUSES = {
  backlog: { label: 'Backlog', color: 'default' },
  todo: { label: 'To Do', color: 'default' },
  in_progress: { label: 'In Progress', color: 'primary' },
  finished: { label: 'Finished', color: 'success' },
  scheduled: { label: 'Scheduled', color: 'info' },
  published: { label: 'Published', color: 'success' },
  archived: { label: 'Archived', color: 'error' }
};

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).length;
};

const ArticleDetailsCard = ({ 
  article, 
  onImageClick, 
  onStatusChange, 
  onPublishDateChange,
  resources,
  onResourcesChange,
  onProjectResourcesChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const renderMobilePreview = () => (
    <Box 
      onClick={handleExpandClick}
      sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'action.hover'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {article.hero_image_url && (
          <Box 
            sx={{ 
              width: 40,
              height: 40,
              borderRadius: 1,
              overflow: 'hidden',
              flexShrink: 0
            }}
            onClick={(e) => {
              e.stopPropagation();
              onImageClick(article.hero_image_url, article.title);
            }}
          >
            <AdaptiveImage
              imageUrl={article.hero_image_url}
              alt={article.title}
              sx={{ 
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        )}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip 
              label={ARTICLE_STATUSES[article.status]?.label || article.status}
              color={ARTICLE_STATUSES[article.status]?.color || 'default'}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              {resources?.length || 0} resources
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {article.publish_date ? new Date(article.publish_date).toLocaleDateString() : 'Not scheduled'} • 
            <Typography 
              component="span" 
              variant="caption" 
              color="text.secondary" 
              sx={{ ml: 0.5 }}
            >
              tap to manage
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {ARTICLE_TYPES[article.type]?.label || article.type}
            {article.subtype && ` • ${ARTICLE_SUBTYPES[article.subtype]?.label || article.subtype}`}
          </Typography>
        </Box>
      </Box>
      <IconButton 
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleExpandClick();
        }}
        sx={{ 
          transition: 'transform 0.2s',
          transform: isExpanded ? 'rotate(180deg)' : 'none'
        }}
      >
        <ExpandMoreIcon />
      </IconButton>
    </Box>
  );

  const renderFullContent = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' }
    }}>
      {/* Main Content */}
      <Box sx={{ 
        flex: '2',
        borderRight: { md: 1 },
        borderColor: 'divider'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          {article.hero_image_url && (
            <Box 
              sx={{ 
                position: 'relative',
                width: { xs: '100%', sm: 120 },
                height: { xs: 180, sm: 'auto' },
                minHeight: { sm: 200 },
                '&:hover': {
                  cursor: 'pointer',
                  '& .zoom-icon': {
                    opacity: 1
                  }
                }
              }}
              onClick={() => onImageClick(article.hero_image_url, article.title)}
            >
              <AdaptiveImage
                imageUrl={article.hero_image_url}
                alt={article.title}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: { xs: '4px 4px 0 0', sm: '4px 0 0 4px' }
                }}
              />
              <Box
                className="zoom-icon"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  opacity: isMobile ? 0.2 : 0,
                  transition: 'opacity 0.2s'
                }}
              >
                <ZoomOutMapIcon sx={{ color: 'white', fontSize: isMobile ? 32 : 24 }} />
              </Box>
            </Box>
          )}
          <Box sx={{ 
            flex: 1, 
            display: 'flex',
            flexDirection: 'column'
          }}>
            <CardContent sx={{ 
              flex: 1, 
              py: { xs: 2, sm: '8px' }, 
              px: { xs: 2, sm: 2 },
              '&:last-child': { pb: { xs: 2, sm: '8px' } }
            }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                mb: 1.5, 
                flexWrap: 'wrap',
                '& .MuiChip-root': {
                  height: isMobile ? '32px' : '24px',
                  '& .MuiChip-label': {
                    px: isMobile ? 1.5 : 1
                  }
                }
              }}>
                <Chip 
                  label={article.topic}
                  size={isMobile ? "medium" : "small"}
                />
                {article.tags?.map((tag) => (
                  <Chip 
                    key={tag}
                    label={tag}
                    size={isMobile ? "medium" : "small"}
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography 
                variant={isMobile ? "body2" : "caption"} 
                color="text.secondary" 
                display="block"
                sx={{ mb: { xs: 1, sm: 0 } }}
              >
                Created {formatDistanceToNow(new Date(article.created_at))} ago • {article.paragraphs.reduce((total, p) => total + countWords(p.content), 0)} words
              </Typography>
              <Typography 
                variant={isMobile ? "body2" : "caption"} 
                color="text.secondary" 
                display="block"
                sx={{ mb: { xs: 1, sm: 0 } }}
              >
                {ARTICLE_TYPES[article.type]?.label || article.type}
                {article.subtype && ` • ${ARTICLE_SUBTYPES[article.subtype]?.label || article.subtype}`}
              </Typography>
            </CardContent>
            <Box sx={{ 
              px: 2,
              py: { xs: 2, sm: 1 },
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 2, sm: 1 }
            }}>
              <FormControl size={isMobile ? "medium" : "small"} sx={{ flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={article.status}
                  label="Status"
                  onChange={onStatusChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: isMobile ? 300 : 200
                      }
                    }
                  }}
                >
                  {Object.entries(ARTICLE_STATUSES).map(([value, { label }]) => (
                    <MenuItem key={value} value={value} sx={{ py: isMobile ? 1.5 : 1 }}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Publish Date"
                type="datetime-local"
                size={isMobile ? "medium" : "small"}
                sx={{ flex: 1 }}
                value={article.publish_date ? new Date(article.publish_date).toISOString().slice(0, 16) : ''}
                onChange={onPublishDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Resources Section */}
      <Box sx={{ 
        flex: '1',
        borderTop: { xs: 1, md: 0 },
        borderColor: 'divider'
      }}>
        <ArticleResources
          article={article}
          resources={resources}
          onResourcesChange={onResourcesChange}
          onProjectResourcesChange={onProjectResourcesChange}
        />
      </Box>
    </Box>
  );

  return (
    <Card 
      sx={{ 
        flex: 2,
        '& .MuiInputBase-root': {
          minHeight: isMobile ? '48px' : 'inherit'
        }
      }} 
      variant="outlined"
    >
      {isMobile ? (
        <>
          {renderMobilePreview()}
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {renderFullContent()}
          </Collapse>
        </>
      ) : (
        renderFullContent()
      )}
    </Card>
  );
};

export default ArticleDetailsCard; 